import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import Modal from '../Modal'
import { iniciarCapsula } from '../../api/client'
import { hora } from './tiempo'

// Putting a person to work on a parte, from anywhere (the board, a parte's
// panel): asignar(persona, parte). Outside working hours the server refuses
// until someone authorizes overtime -- this asks, then retries. What
// happened (including "X's work on #N was paused") shows in a short notice.
// Render `ui` wherever the hook is used. persona: { id, nombre };
// parte: { id, numero, unidad_codigo }.
export function useAsignar({ onHecho } = {}) {
  const { t } = useTranslation('engineering')
  const [pendiente, setPendiente] = useState(null)    // { persona, parte, jornada } awaiting authorization
  const [ocupado, setOcupado] = useState(false)
  const [aviso, setAviso] = useState(null)            // { texto, error }

  useEffect(() => {
    if (!aviso) return
    const id = setTimeout(() => setAviso(null), aviso.error ? 7000 : 5000)
    return () => clearTimeout(id)
  }, [aviso])

  const intentar = async (persona, parte, autorizar = false) => {
    setOcupado(true)
    try {
      const r = await iniciarCapsula({ persona_id: persona.id, parte_id: parte.id, autorizar_hora_extra: autorizar })
      setPendiente(null)
      const base = t('capsulas.aviso.asignada', { persona: persona.nombre, numero: parte.numero })
      setAviso({
        texto: r.pausada
          ? `${base} ${t('capsulas.aviso.pausada', { numero: r.pausada.parte_numero })}`
          : base + (r.capsula.hora_extra ? ` ${t('capsulas.aviso.extra')}` : ''),
      })
      onHecho?.(r)
    } catch (err) {
      const d = err.response?.data?.detail
      if (d?.code === 'fuera_de_horario') setPendiente({ persona, parte, jornada: d.jornada })
      else if (d?.code === 'ya_trabajando') setAviso({ texto: t('capsulas.aviso.yaTrabajando', { persona: persona.nombre, numero: parte.numero }) })
      else if (d?.code === 'parte_cerrado') setAviso({ texto: t('capsulas.aviso.parteCerrado', { numero: parte.numero }), error: true })
      else setAviso({ texto: t('capsulas.aviso.fallo'), error: true })
    }
    setOcupado(false)
  }

  const ui = (
    <>
      {pendiente && (
        <Modal title={t('capsulas.extra.titulo')} onClose={() => setPendiente(null)}>
          <div className="space-y-4 text-sm">
            <p className="text-gray-700">
              {pendiente.jornada
                ? t('capsulas.extra.fuera', { inicio: hora(pendiente.jornada.inicio), fin: hora(pendiente.jornada.fin) })
                : t('capsulas.extra.noLaboral')}
            </p>
            <p className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-amber-900">
              {t('capsulas.extra.pregunta', { persona: pendiente.persona.nombre, numero: pendiente.parte.numero })}
            </p>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setPendiente(null)}
                className="bg-white border border-gray-200 text-gray-600 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-50">
                {t('unidades.cancel')}
              </button>
              <button type="button" disabled={ocupado} onClick={() => intentar(pendiente.persona, pendiente.parte, true)}
                className="bg-amber-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-amber-700 disabled:opacity-50">
                {t('capsulas.extra.autorizar')}
              </button>
            </div>
          </div>
        </Modal>
      )}
      {aviso && (
        <div role="status" className={`fixed bottom-6 right-6 z-50 max-w-sm rounded-xl shadow-lg px-4 py-3 text-sm ${
          aviso.error ? 'bg-red-600 text-white' : 'bg-gray-900 text-white'}`}>
          {aviso.texto}
        </div>
      )}
    </>
  )

  return { asignar: (persona, parte) => intentar(persona, parte), ocupado, ui }
}
